import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { DollarSign, Key, Loader2, X } from 'lucide-react';

const SuperAdminBilling = () => {
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [formLoading, setFormLoading] = useState(false);

    // License Form State
    const [selectedProject, setSelectedProject] = useState(null);
    const [licensePlanType, setLicensePlanType] = useState('Pro');
    const [licenseValidUntil, setLicenseValidUntil] = useState('');
    const [licenseStatus, setLicenseStatus] = useState('active');
    const [licenseModalOpen, setLicenseModalOpen] = useState(false);

    const headers = {
        Authorization: `Bearer ${localStorage.getItem('token')}`
    };

    const fetchOverview = async () => {
        try {
            const response = await axios.get('http://localhost:4000/api/superadmin/overview', { headers });
            setCompanies(response.data);
        } catch (err) {
            console.error("Error loading SuperAdmin overview:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOverview();
    }, []);

    const handleUpdateLicense = async (e) => {
        e.preventDefault();
        if (!selectedProject || !licensePlanType || !licenseValidUntil || !licenseStatus) return;
        setFormLoading(true);
        try {
            await axios.put('http://localhost:4000/api/superadmin/licenses', {
                project_id: selectedProject.id,
                plan_type: licensePlanType,
                valid_until: licenseValidUntil,
                status: licenseStatus
            }, { headers });

            setLicenseModalOpen(false);
            setSelectedProject(null);
            fetchOverview();
            alert('Licencia actualizada correctamente.');
        } catch (err) {
            alert(err.response?.data?.error || 'Error al actualizar la licencia.');
        } finally {
            setFormLoading(false);
        }
    };

    const formatExpiryDate = (dateStr) => {
        if (!dateStr) return 'Sin vigencia';
        try {
            const d = new Date(dateStr);
            if (isNaN(d.getTime())) return 'Fecha inválida';
            return d.toLocaleDateString('es-MX', {
                year: 'numeric',
                month: 'short',
                day: '2-digit'
            });
        } catch (e) {
            return 'Fecha inválida';
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-theme-bg flex items-center justify-center">
                <div className="relative">
                    <div className="w-16 h-16 border-4 border-emerald-500/20 rounded-full"></div>
                    <div className="w-16 h-16 border-4 border-emerald-500 rounded-full border-t-transparent animate-spin absolute top-0 left-0"></div>
                </div>
            </div>
        );
    }

    const projectsList = companies.flatMap(c => c.projects.map(p => ({ ...p, companyName: c.name })));

    return (
        <div className="min-h-screen bg-theme-bg text-theme-text-primary p-4 md:p-8 font-sans pb-20">
            <div className="max-w-7xl mx-auto space-y-6">
                
                {/* Header */}
                <div className="border-b border-theme-border/80 pb-6">
                    <h1 className="text-3xl font-extrabold tracking-tight text-theme-text-primary flex items-center gap-3">
                        <DollarSign className="text-emerald-500" size={32} /> Facturación SaaS
                    </h1>
                    <p className="text-theme-text-secondary text-xs mt-1">Monitoreo de ingresos recurrentes mensuales (MRR) y gestión de licenciamiento por obra.</p>
                </div>

                {/* Resumen Financiero Facturación */}
                <div className="bg-theme-panel/60 backdrop-blur-xl border border-theme-border rounded-3xl p-6 relative overflow-hidden shadow-xl">
                    <div className="absolute right-0 top-0 w-32 h-32 bg-emerald-500/5 rounded-full blur-2xl"></div>
                    <h3 className="text-lg font-bold text-theme-text-primary mb-4 flex items-center gap-2">
                        <DollarSign size={20} className="text-emerald-450" /> Resumen de Ingresos SaaS (Simulado)
                    </h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="bg-theme-bg p-4 rounded-2xl border border-theme-border">
                            <span className="text-[10px] font-bold text-theme-text-secondary uppercase tracking-wider block">Ingreso Recurrente Estimado (MRR)</span>
                            <span className="text-2xl font-black text-emerald-400 mt-1 block">
                                ${(() => {
                                    let total = 0;
                                    companies.forEach(c => {
                                        c.projects.forEach(p => {
                                            if (p.licenseStatus === 'active') {
                                                if (p.planType === 'Basic') total += 49;
                                                else if (p.planType === 'Pro') total += 149;
                                                else if (p.planType === 'Enterprise') total += 499;
                                            }
                                        });
                                    });
                                    return total.toLocaleString();
                                })()} USD/mes
                            </span>
                        </div>
                        <div className="bg-theme-bg p-4 rounded-2xl border border-theme-border">
                            <span className="text-[10px] font-bold text-theme-text-secondary uppercase tracking-wider block">Planes Enterprise ($499/mes)</span>
                            <span className="text-2xl font-black text-theme-text-primary mt-1 block">
                                {projectsList.filter(p => p.planType === 'Enterprise' && p.licenseStatus === 'active').length} Activos
                            </span>
                        </div>
                        <div className="bg-theme-bg p-4 rounded-2xl border border-theme-border">
                            <span className="text-[10px] font-bold text-theme-text-secondary uppercase tracking-wider block">Planes Pro ($149/mes)</span>
                            <span className="text-2xl font-black text-theme-text-primary mt-1 block">
                                {projectsList.filter(p => p.planType === 'Pro' && p.licenseStatus === 'active').length} Activos
                            </span>
                        </div>
                        <div className="bg-theme-bg p-4 rounded-2xl border border-theme-border">
                            <span className="text-[10px] font-bold text-theme-text-secondary uppercase tracking-wider block">Planes Basic ($49/mes)</span>
                            <span className="text-2xl font-black text-theme-text-primary mt-1 block">
                                {projectsList.filter(p => p.planType === 'Basic' && p.licenseStatus === 'active').length} Activos
                            </span>
                        </div>
                    </div>
                </div>

                {/* Tabla de Suscripciones por Cliente */}
                <div className="bg-theme-panel/60 backdrop-blur-xl border border-theme-border rounded-3xl p-6 shadow-xl overflow-hidden">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-bold text-theme-text-primary">Detalle de Suscripciones Activas</h3>
                        <span className="text-xs text-theme-text-secondary font-medium">Lista consolidada de proyectos con licencia</span>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs border-collapse">
                            <thead>
                                <tr className="border-b border-theme-border text-theme-text-secondary font-bold uppercase tracking-wider">
                                    <th className="pb-3 pl-2">Cliente / Empresa</th>
                                    <th className="pb-3">Proyecto / Obra</th>
                                    <th className="pb-3">Tipo de Plan</th>
                                    <th className="pb-3">Llave de Licencia</th>
                                    <th className="pb-3">Vencimiento</th>
                                    <th className="pb-3">Cámaras</th>
                                    <th className="pb-3">Estado</th>
                                    <th className="pb-3 pr-2 text-right">Acción</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-theme-border/60 text-theme-text-primary">
                                {projectsList.map(project => {
                                    return (
                                        <tr key={project.id} className="hover:bg-theme-border/10 transition-colors">
                                            <td className="py-3.5 pl-2 font-bold text-theme-text-primary">{project.companyName}</td>
                                            <td className="py-3.5 text-theme-text-primary">{project.name}</td>
                                            <td className="py-3.5">
                                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                                    project.planType === 'Enterprise' ? 'bg-purple-500/10 text-purple-400 border border-purple-500/20' :
                                                    project.planType === 'Pro' ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20' :
                                                    'bg-theme-text-secondary/10 text-theme-text-secondary border border-theme-border'
                                                }`}>
                                                    {project.planType || 'Pro'}
                                                </span>
                                            </td>
                                            <td className="py-3.5 font-mono text-[10px] text-theme-text-secondary select-all">{project.licenseKey || 'N/A'}</td>
                                            <td className="py-3.5 text-theme-text-secondary">{formatExpiryDate(project.validUntil)}</td>
                                            <td className="py-3.5 font-mono font-bold text-theme-text-primary">{project.totalCameras}</td>
                                            <td className="py-3.5">
                                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                                    project.licenseStatus === 'active' ? 'bg-emerald-500/10 text-emerald-450 border border-emerald-500/20' : 'bg-rose-500/10 text-rose-400 border border-rose-500/20'
                                                }`}>
                                                    {project.licenseStatus === 'active' ? 'Activo' : 'Expirado'}
                                                </span>
                                            </td>
                                            <td className="py-3.5 pr-2 text-right">
                                                <button
                                                    onClick={() => {
                                                        setSelectedProject(project);
                                                        setLicensePlanType(project.planType || 'Pro');
                                                        setLicenseValidUntil(project.validUntil ? project.validUntil.split('T')[0] : '');
                                                        setLicenseStatus(project.licenseStatus || 'active');
                                                        setLicenseModalOpen(true);
                                                    }}
                                                    className="bg-purple-600/15 border border-purple-500/25 hover:bg-purple-600/35 text-purple-400 px-3 py-1 rounded-xl text-[10px] font-bold transition-all cursor-pointer"
                                                >
                                                    Gestionar
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* MODAL: GESTIONAR LICENCIA */}
            {licenseModalOpen && selectedProject && (
                <div className="fixed inset-0 bg-black/85 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-theme-panel border border-theme-border rounded-3xl max-w-md w-full p-6 shadow-2xl space-y-6 relative animate-fade-in">
                        <button onClick={() => { setLicenseModalOpen(false); setSelectedProject(null); }} className="absolute right-4 top-4 text-theme-text-secondary hover:text-theme-text-primary cursor-pointer">
                            <X size={20} />
                        </button>
                        <div className="space-y-1">
                            <h3 className="text-xl font-bold text-theme-text-primary flex items-center gap-2">
                                <Key className="text-purple-400" size={22} /> Gestionar Licencia
                            </h3>
                            <p className="text-xs text-theme-text-secondary">Modifica el plan o vigencia para la obra: <span className="font-bold text-theme-text-primary">{selectedProject.name}</span></p>
                        </div>
                        <form onSubmit={handleUpdateLicense} className="space-y-4">
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-black uppercase text-theme-text-secondary tracking-wider">Plan de Suscripción</label>
                                <select
                                    value={licensePlanType}
                                    onChange={(e) => setLicensePlanType(e.target.value)}
                                    className="w-full bg-theme-bg border border-theme-border rounded-xl p-3 text-sm text-theme-text-primary focus:outline-none focus:border-purple-500/50"
                                >
                                    <option value="Básico">Básico ($49 USD/mes)</option>
                                    <option value="Pro">Pro ($149 USD/mes)</option>
                                    <option value="Enterprise">Enterprise ($499 USD/mes)</option>
                                </select>
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-black uppercase text-theme-text-secondary tracking-wider">Fecha de Vencimiento</label>
                                <input
                                    type="date"
                                    required
                                    value={licenseValidUntil}
                                    onChange={(e) => setLicenseValidUntil(e.target.value)}
                                    className="w-full bg-theme-bg border border-theme-border rounded-xl p-3 text-sm text-theme-text-primary focus:outline-none focus:border-purple-500/50"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-black uppercase text-theme-text-secondary tracking-wider">Estado de la Licencia</label>
                                <select
                                    value={licenseStatus}
                                    onChange={(e) => setLicenseStatus(e.target.value)}
                                    className="w-full bg-theme-bg border border-theme-border rounded-xl p-3 text-sm text-theme-text-primary focus:outline-none focus:border-purple-500/50"
                                >
                                    <option value="active">Activo / Permitir Acceso</option>
                                    <option value="expired">Expirado / Bloquear Acceso</option>
                                </select>
                            </div>
                            <button
                                type="submit"
                                disabled={formLoading}
                                className="w-full bg-purple-600 hover:bg-purple-500 disabled:bg-theme-bg text-white font-bold p-3 rounded-xl text-xs flex justify-center items-center gap-2 shadow-lg shadow-purple-600/10 cursor-pointer"
                            >
                                {formLoading ? <Loader2 className="animate-spin" size={16} /> : 'Guardar Cambios de Licencia'}
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SuperAdminBilling;
